import { useState } from 'react';
import type { ShareData } from '../../utils/seo/socialSharing';
import {
  getFacebookShareUrl,
  getTwitterShareUrl,
  getLinkedInShareUrl,
  getWhatsAppShareUrl,
  getEmailShareUrl,
  openShareWindow,
  copyToClipboard,
  nativeShare,
  SOCIAL_SHARING_CONFIG
} from '../../utils/seo/socialSharing';

// SVG Icons as React components
const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.531 3.488"/>
  </svg>
);

const EmailIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-.904.732-1.636 1.636-1.636h1.811l8.553 6.545 8.553-6.545h1.811A1.636 1.636 0 0 1 24 5.457z"/>
  </svg>
);

const CopyIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

const ShareIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.50-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92S19.61 16.08 18 16.08z"/>
  </svg>
);

interface SocialShareProps {
  data?: Partial<ShareData>;
  variant?: 'default' | 'compact' | 'buttons-only';
  showLabel?: boolean;
  className?: string;
}

export function SocialShare({ 
  data, 
  variant = 'default', 
  showLabel = true,
  className = '' 
}: SocialShareProps) {
  const [copied, setCopied] = useState(false);
  
  const shareData: ShareData = {
    title: data?.title || SOCIAL_SHARING_CONFIG.defaultTitle,
    description: data?.description || SOCIAL_SHARING_CONFIG.defaultDescription,
    url: data?.url || SOCIAL_SHARING_CONFIG.baseUrl,
    hashtags: data?.hashtags || SOCIAL_SHARING_CONFIG.defaultHashtags
  };

  const handleShare = async (platform: string, url: string) => {
    if (platform === 'native') {
      const shared = await nativeShare(shareData);
      if (!shared) {
        // Fallback to copy to clipboard
        await handleCopyToClipboard();
      }
      return;
    }

    if (platform === 'copy') {
      await handleCopyToClipboard();
      return;
    }

    openShareWindow(url, platform);
  };

  const handleCopyToClipboard = async () => {
    const success = await copyToClipboard(shareData);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareButtons = [
    {
      name: 'Facebook',
      icon: FacebookIcon,
      url: getFacebookShareUrl(shareData),
      color: 'bg-[#1877F2] hover:bg-[#166FE5] text-white'
    },
    {
      name: 'Twitter',
      icon: TwitterIcon,
      url: getTwitterShareUrl(shareData),
      color: 'bg-[#1DA1F2] hover:bg-[#1A91DA] text-white'
    },
    {
      name: 'LinkedIn',
      icon: LinkedInIcon,
      url: getLinkedInShareUrl(shareData),
      color: 'bg-[#0A66C2] hover:bg-[#004182] text-white'
    },
    {
      name: 'WhatsApp',
      icon: WhatsAppIcon,
      url: getWhatsAppShareUrl(shareData),
      color: 'bg-[#25D366] hover:bg-[#128C7E] text-white'
    },
    {
      name: 'Email',
      icon: EmailIcon,
      url: getEmailShareUrl(shareData),
      color: 'bg-gray-600 hover:bg-gray-700 text-white'
    }
  ];

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        {showLabel && (
          <span className="text-sm font-medium text-gray-600 hidden sm:inline">Share:</span>
        )}
        <div className="flex gap-2">
          {shareButtons.map((button) => {
            const IconComponent = button.icon;
            return (
              <button
                key={button.name}
                onClick={() => handleShare(button.name.toLowerCase(), button.url)}
                className={`w-10 h-10 rounded-lg ${button.color} flex items-center justify-center transition-colors shadow-sm hover:shadow-md`}
                title={`Share on ${button.name}`}
                aria-label={`Share on ${button.name}`}
              >
                <IconComponent />
              </button>
            );
          })}
          <button
            onClick={() => handleShare('copy', '')}
            className="w-10 h-10 rounded-lg bg-gray-500 hover:bg-gray-600 text-white flex items-center justify-center transition-colors shadow-sm hover:shadow-md"
            title={copied ? 'Copied!' : 'Copy link'}
            aria-label="Copy link to clipboard"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
        </div>
      </div>
    );
  }

  if (variant === 'buttons-only') {
    return (
      <div className={`flex flex-wrap gap-3 ${className}`}>
        {shareButtons.map((button) => {
          const IconComponent = button.icon;
          return (
            <button
              key={button.name}
              onClick={() => handleShare(button.name.toLowerCase(), button.url)}
              className={`px-4 py-2.5 rounded-lg ${button.color} text-sm font-medium transition-colors flex items-center gap-2 shadow-sm hover:shadow-md`}
              aria-label={`Share on ${button.name}`}
            >
              <IconComponent />
              <span>{button.name}</span>
            </button>
          );
        })}
        <button
          onClick={() => handleShare('copy', '')}
          className="px-4 py-2.5 rounded-lg bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium transition-colors flex items-center gap-2 shadow-sm hover:shadow-md"
          aria-label="Copy link to clipboard"
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
          <span>{copied ? 'Copied!' : 'Copy Link'}</span>
        </button>
      </div>
    );
  }

  // Default variant - full layout
  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      {showLabel && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Share Your Results
        </h3>
      )}
      
      <div className="space-y-4">
        {/* Native share button (mobile) */}
        {'share' in navigator && (
          <button
            onClick={() => handleShare('native', '')}
            className="w-full px-4 py-3 bg-sa-green text-white rounded-lg hover:bg-sa-green/90 transition-colors flex items-center justify-center gap-2 md:hidden shadow-sm"
          >
            <ShareIcon />
            <span>Share</span>
          </button>
        )}
        
        {/* Social media buttons */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {shareButtons.map((button) => {
            const IconComponent = button.icon;
            return (
              <button
                key={button.name}
                onClick={() => handleShare(button.name.toLowerCase(), button.url)}
                className={`px-3 py-3 rounded-lg ${button.color} text-sm font-medium transition-colors flex flex-col items-center justify-center gap-2 shadow-sm hover:shadow-md`}
                aria-label={`Share on ${button.name}`}
              >
                <IconComponent />
                <span className="text-xs">{button.name}</span>
              </button>
            );
          })}
        </div>
        
        {/* Copy link button */}
        <button
          onClick={() => handleShare('copy', '')}
          className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm"
          aria-label="Copy link to clipboard"
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
          <span>{copied ? 'Link Copied!' : 'Copy Link'}</span>
        </button>
      </div>
    </div>
  );
}

export default SocialShare;